import { IDestroyOptions, Point, Rectangle, Sprite } from "pixi.js";
import { BaseScalableView } from "../../../common/BaseScalableView";
import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { PromiseCompletionSource } from "by/gamefactory/shared/async/PromiseCompletionSource";
import { PiggyResourcePackage } from "../resources/PiggyResourcePackage";
import { FullscreenInputBlocker } from "../../../game/components/FullscreenInputBlocker";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { ButtonWithScaleAnimation } from "../../../common/components/ButtonWithScaleAnimation";
import { gsap } from "gsap";
import { IPiggyController } from "../controller/IPiggyController";
import { CurrentPiggyContentView, CurrentPiggyState } from "../components/piggycontentview/CurrentPiggyContentView";
import { NextPiggyContentView } from "../components/piggycontentview/NextPiggyContentView";
import { IPlayerModel } from "../../../common/models/IPlayerModel";
import { IPiggyBankModel } from "../../../common/models/piggybank/IPiggyBankModel";
import { NoCacheDynamicPackage } from "../../../common/resources/NoCacheDynamicPackage";
import { TickerDelay } from "../../../../../tools/TickerDelay";
import { PiggyRewardCollectAnimationView } from "../../../common/components/animations/PiggyRewardCollectAnimationView";
import { NotificationLayerProvider } from "by/gamefactory/cityversetycoon/composition/providers/NotificationLayerProvider";
import { TopPanel } from "../../../game/components/toppanel/TopPanel";
import { PiggyBreakBoneAnimationResourcePackage } from "../resources/PiggyBreakBoneAnimationResourcePackage";

export class PiggyPopupView extends BaseScalableView<void> {
    private static readonly MOVE_DISTANCE_OFFSET_X = 300;

    private readonly piggyResources: PiggyResourcePackage;
    private readonly commonResources: CommonResourcesPackage;
    private readonly piggyDynamicResources: NoCacheDynamicPackage;
    private readonly animations: gsap.core.Timeline;
    private readonly piggyController: IPiggyController;
    private readonly playerModel: IPlayerModel;
    private readonly piggyModel: IPiggyBankModel;
    private readonly piggyState: CurrentPiggyState;
    private readonly notificationLayerProvider: NotificationLayerProvider;
    private readonly piggyBreakBoneAnimationResources?: PiggyBreakBoneAnimationResourcePackage;

    private viewBackground: Sprite;
    private inputBlocker: FullscreenInputBlocker;
    private closeButton: ButtonWithScaleAnimation;
    private leftArrow: ButtonWithScaleAnimation;
    private rightArrow: ButtonWithScaleAnimation;
    private currentPiggyView: CurrentPiggyContentView;
    private nextPiggyView: NextPiggyContentView;
    private nextPiggyIsCurrent: boolean;
    private topPanel: TopPanel;

    public constructor(
        operationControls: PromiseCompletionSource<void>,
        canvasScaler: ICanvasScaler,
        piggyResources: PiggyResourcePackage,
        commonResources: CommonResourcesPackage,
        piggyDynamicResources: NoCacheDynamicPackage,
        piggyController: IPiggyController,
        playerModel: IPlayerModel,
        piggyModel: IPiggyBankModel,
        piggyState: CurrentPiggyState,
        notificationLayerProvider: NotificationLayerProvider,
        topPanel: TopPanel,
        piggyBreakBoneAnimationResources?: PiggyBreakBoneAnimationResourcePackage
    ) {
        super(operationControls, canvasScaler);
        this.piggyResources = piggyResources;
        this.commonResources = commonResources;
        this.piggyDynamicResources = piggyDynamicResources;
        this.piggyController = piggyController;
        this.playerModel = playerModel;
        this.piggyModel = piggyModel;
        this.piggyState = piggyState;
        this.notificationLayerProvider = notificationLayerProvider;
        this.topPanel = topPanel;
        this.piggyBreakBoneAnimationResources = piggyBreakBoneAnimationResources;
        this.animations = new gsap.core.Timeline({ paused: true });
        this.nextPiggyIsCurrent = false;
        this.build();
        this.animations.fromTo(this.viewBackground, { alpha: 0 }, { alpha: 1, duration: 0.4, ease: "power1.in" });
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.closeButton.onUp.add(this.onCloseButtonClickHandle, this);
        this.leftArrow.onUp.add(this.onLeftArrowClickHandle, this);
        this.rightArrow.onUp.add(this.onRightArrowClickHandle, this);
    }

    private build(): void {
        this.viewBackground = new Sprite(this.commonResources.assets_common_blured_backgrounds_blurry_lobby_jpg);
        this.viewBackground.eventMode = 'static';
        this.closeButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_x_png);
        this.closeButton.hitArea = new Rectangle(-this.closeButton.width * 0.5, -this.closeButton.height, this.closeButton.width * 2, this.closeButton.height * 2);
        this.leftArrow = new ButtonWithScaleAnimation(this.commonResources.assets_common_ui_back_png);
        this.leftArrow.hitArea = new Rectangle(-this.leftArrow.width, -this.leftArrow.height, this.leftArrow.width * 3, this.leftArrow.height * 3);
        this.rightArrow = new ButtonWithScaleAnimation(this.commonResources.assets_common_ui_back_png);
        this.rightArrow.hitArea = new Rectangle(-this.rightArrow.width, -this.rightArrow.height, this.rightArrow.width * 3, this.rightArrow.height * 3);
        this.rightArrow.scale.x = -1;
        this.currentPiggyView = new CurrentPiggyContentView(
            this.piggyResources,
            this.piggyDynamicResources,
            this.commonResources,
            this.piggyController,
            this.piggyDynamicResources.resource(this.piggyModel.imageUrl.value),
            this.piggyState,
            this.playerModel,
            this.canvasScaler,
            this.piggyModel,
            PiggyPopupView.MOVE_DISTANCE_OFFSET_X,
            this.piggyState,
            this.piggyBreakBoneAnimationResources
        );
        if (this.piggyState === CurrentPiggyState.UNLOCKED) {
            this.currentPiggyView.onCollectButtonClick.add(this.onCollectButtonClickHandle, this);
        }
        this.nextPiggyView = new NextPiggyContentView(
            this.piggyResources,
            this.piggyController,
            this.canvasScaler,
            PiggyPopupView.MOVE_DISTANCE_OFFSET_X
        );
        this.inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.inputBlocker.blocked = false;

        this.addChild(
            this.viewBackground,
            this.closeButton,
            this.leftArrow,
            this.rightArrow,
            this.currentPiggyView,
            this.nextPiggyView,
            this.inputBlocker
        );
    }

    private layout(_width: number, _height: number): void {
        this.closeButton.position.set(696, 395);
        this.leftArrow.position.set(30, 1510);
        this.rightArrow.position.set(800, 1440);
    }

    private async show(): Promise<void> {
        this.closeButton.visible = this.piggyState != CurrentPiggyState.UNLOCKED;
        this.rightArrow.visible = this.piggyState != CurrentPiggyState.UNLOCKED;
        this.leftArrow.visible = false;
        this.nextPiggyView.position.x = this.canvasScaler.referenceWidth + PiggyPopupView.MOVE_DISTANCE_OFFSET_X;
        this.nextPiggyView.visible = false;
        this.animations.play();
        await this.currentPiggyView.playShowAnimation();
    }

    private async hide(): Promise<void> {
        TickerDelay.delayCallback(() => this.animations.reverse(), 400);
        if (!this.nextPiggyIsCurrent) {
            await this.currentPiggyView.playHideAnimation();
        } else {
            await this.nextPiggyView.playHideAnimation();
        }
    }

    private async onCloseButtonClickHandle(): Promise<void> {
        this.inputBlocker.blocked = true;
        await this.hide();
        this.inputBlocker.blocked = false;
        this.operationControls.complete();
    }

    private async onCollectButtonClickHandle(): Promise<void> {
        this.inputBlocker.blocked = true;
        await this.currentPiggyView.breakPiggy();
        const piggyRewardCollectAnimationView = new PiggyRewardCollectAnimationView(
            this.piggyResources.assets_views_piggy_token_png,
            this.piggyDynamicResources.resource(this.piggyModel.piggyBankBenefistAtMinHours.imageUrl),
            new Point(570, this.topPanel.y),
            new Point(640, 1550)
        );
        this.notificationLayerProvider.notificationLayer.addChild(piggyRewardCollectAnimationView);
        this.currentPiggyView.hideRewards();
        piggyRewardCollectAnimationView.startAnimation();
        await TickerDelay.delay(400);
        await Promise.all([
            this.hide(),
            this.playerModel.addTokens(this.piggyModel.tokensBalance.value)
        ]);
        this.inputBlocker.blocked = false;
        this.operationControls.complete();
    }

    private async onLeftArrowClickHandle(): Promise<void> {
        this.inputBlocker.blocked = true;
        this.closeButton.visible = false;
        this.currentPiggyView.visible = true;
        this.leftArrow.visible = false;
        await this.playToCurrentPiggyAnimation();
        this.nextPiggyView.visible = false;
        this.rightArrow.visible = true;
        this.closeButton.visible = true;
        this.nextPiggyIsCurrent = false;
        this.inputBlocker.blocked = false;
    }

    private async onRightArrowClickHandle(): Promise<void> {
        this.inputBlocker.blocked = true;
        this.closeButton.visible = false;
        this.nextPiggyView.visible = true;
        this.rightArrow.visible = false;
        await this.playToNextPiggyAnimation();
        this.currentPiggyView.visible = false;
        this.leftArrow.visible = true;
        this.closeButton.visible = true;
        this.nextPiggyIsCurrent = true;
        this.inputBlocker.blocked = false;
    }

    private async playToNextPiggyAnimation(): Promise<void> {
        await Promise.all([
            this.currentPiggyView.playMoveLeftAnimation(),
            this.nextPiggyView.playMoveLeftAnimation()
        ]);
    }

    private async playToCurrentPiggyAnimation(): Promise<void> {
        await Promise.all([
            this.currentPiggyView.playMoveRightAnimation(),
            this.nextPiggyView.playMoveRightAnimation()
        ]);
    }

    public async start(): Promise<void> {
        await this.show();
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.animations?.kill();
        this.closeButton.onUp.remove(this.onCloseButtonClickHandle, this);
        this.leftArrow.onUp.remove(this.onLeftArrowClickHandle, this);
        this.rightArrow.onUp.remove(this.onRightArrowClickHandle, this);
        if (this.piggyState === CurrentPiggyState.UNLOCKED) {
            this.currentPiggyView.onCollectButtonClick?.remove(this.onCollectButtonClickHandle, this);
        }
        super.destroy(options);
    }
}
